import { store } from '../stores/store'
import {
    footstepEffectTypeKeys,
    gearItems,
    type FootstepEffectType,
} from '../game/effects/footstepConfig'
import { gearNames } from '../game/models/character/characterGear'
import { CHAMELEON_TEXTURE_PRESETS } from '../game/models/powerups/characterCustomizationOptions'
import {
    chameleonTextureNumToStoreString,
    chameleonTextureStringToNum,
    colorStringToRgbUint32,
    decodeNumericShareTuple,
    encodeNumericShareTuple,
    rgbUint32ToHexColor,
} from './sharePayloadCodec'

export const CHARACTER_SHARE_TUPLE_LENGTH = 6 as const

const MAX_CHAMELEON_TEXTURE = CHAMELEON_TEXTURE_PRESETS.length
const MAX_GEAR_LEVEL = gearItems.length

export type CharacterSharePayload = {
    chameleonColor: string
    chameleonTexture: string
    footstepEffect: FootstepEffectType
    decorationItems: string[]
    unlockedGearLevel: number
    selectedGearItems: number[]
}

function selectedGearItemsToBitmask(indices: Iterable<number>): number {
    let mask = 0
    for (const index of indices) {
        if (Number.isInteger(index) && index >= 0 && index < 31) {
            mask |= 1 << index
        }
    }
    return mask >>> 0
}

function bitmaskToSelectedGearItems(mask: number, unlockedGearLevel: number): number[] {
    const value = Math.max(0, Math.floor(mask)) >>> 0
    const indices: number[] = []
    for (let index = 0; index < MAX_GEAR_LEVEL; index++) {
        if ((value & (1 << index)) !== 0 && index < unlockedGearLevel) {
            indices.push(index)
        }
    }
    return indices
}

function selectedDecorationItemsToBitmask(items: Iterable<string>): number {
    let mask = 0
    for (const item of items) {
        const index = gearNames.indexOf(item)
        if (index >= 0 && index < 31) {
            mask |= 1 << index
        }
    }
    return mask >>> 0
}

function bitmaskToSelectedDecorationItems(mask: number): string[] {
    const value = Math.max(0, Math.floor(mask)) >>> 0
    const items: string[] = []
    for (let index = 0; index < gearNames.length; index++) {
        if ((value & (1 << index)) !== 0) {
            items.push(gearNames[index]!)
        }
    }
    return items
}

function buildCharacterShareTuple(): number[] {
    const footstepIndex = footstepEffectTypeKeys.indexOf(store.footstepEffect)
    return [
        colorStringToRgbUint32(store.chameleonColor),
        chameleonTextureStringToNum(store.chameleonTexture),
        footstepIndex,
        selectedDecorationItemsToBitmask(store.selectedDecorationItems),
        store.unlockedGearLevel,
        selectedGearItemsToBitmask(store.selectedGearItems),
    ]
}

function characterShareTupleToPayload(tuple: number[]): CharacterSharePayload | null {
    const u = tuple

    const rawFootstepIndex = Math.floor(u[2])
    const unlockedGearLevel = Math.max(0, Math.min(MAX_GEAR_LEVEL, Math.floor(u[4])))

    return {
        chameleonColor: rgbUint32ToHexColor(u[0]),
        chameleonTexture: chameleonTextureNumToStoreString(u[1], {
            min: 1,
            max: MAX_CHAMELEON_TEXTURE,
        }),
        footstepEffect:
            rawFootstepIndex >= 0 &&
            rawFootstepIndex < footstepEffectTypeKeys.length
                ? footstepEffectTypeKeys[rawFootstepIndex]
                : '',
        decorationItems: bitmaskToSelectedDecorationItems(u[3]),
        unlockedGearLevel,
        selectedGearItems: bitmaskToSelectedGearItems(u[5], unlockedGearLevel),
    }
}

// Encodes the current store character config + gained gear into a `characterID` token.
export function encodeCharacterIdQueryValue(): string {
    return encodeNumericShareTuple(buildCharacterShareTuple())
}

export function applyCharacterSharePayloadToStore(payload: CharacterSharePayload): void {
    store.setChameleonColor(payload.chameleonColor)
    store.setChameleonTexture(payload.chameleonTexture)
    store.setFootstepEffect(payload.footstepEffect)
    store.setSelectedDecorationItems(payload.decorationItems)
    store.setUnlockedGearLevel(payload.unlockedGearLevel)
    store.setSelectedGearItems(payload.selectedGearItems)
}

export function decodeCharacterSharePayload(
    characterIdParamValue: string,
): CharacterSharePayload | null {
    return decodeNumericShareTuple(
        characterIdParamValue,
        CHARACTER_SHARE_TUPLE_LENGTH,
        characterShareTupleToPayload,
    )
}
